import { EventGroup } from '@/api/enumerations';
import {
  getOversightClientsById,
  getOversightUsersById,
} from '@/api/oversight';
import { useEffect, useRef, useState } from 'react';

export type EntityRef = {
  group: string;
  id: number;
};

// Route groups whose `{id}` stands for a person. The accordion header carries
// that name so whoever reviews the session later can tell who was being looked
// at without opening the group — the id alone is not on screen anywhere.
const entityNameFetchers: Record<string, (id: number) => Promise<string>> = {
  'oversight/clients/customer-details/{id}': async (id) => {
    const { data, detail } = await getOversightClientsById(
      id,
      EventGroup.BACKGROUND
    );

    return detail?.description ? '' : data?.name ?? '';
  },
  'oversight/administrative-users/details/{id}': async (id) => {
    const { data, detail } = await getOversightUsersById(
      id,
      EventGroup.BACKGROUND
    );

    return detail?.description ? '' : data?.name ?? '';
  },
};

const KEY_SEPARATOR = '#';

export const isNamedEntityGroup = (group: string): boolean =>
  group in entityNameFetchers;

// Ids repeat across entity types, so the group has to be part of the key:
// client 5 and administrative user 5 are different people.
export const entityNameKey = ({ group, id }: EntityRef): string =>
  `${group}${KEY_SEPARATOR}${id}`;

// Naming the accordions is chrome, not user activity: these lookups are tagged
// as background so they do not surface as events in a later timeline.
export function useEntityNames(entities: EntityRef[]): Record<string, string> {
  const [names, setNames] = useState<Record<string, string>>({});
  const requestedKeys = useRef<Set<string>>(new Set());

  // Keyed by a string so a fresh array on every render does not refire the
  // effect; only a genuinely new set of entities does.
  const entitiesKey = entities.map(entityNameKey).join(',');

  useEffect(() => {
    let active = true;

    const missingKeys = entitiesKey
      .split(',')
      .filter(Boolean)
      .filter((key) => !requestedKeys.current.has(key));

    if (missingKeys.length > 0) {
      missingKeys.forEach((key) => requestedKeys.current.add(key));

      Promise.all(
        missingKeys.map(async (key): Promise<[string, string]> => {
          const separatorIndex = key.lastIndexOf(KEY_SEPARATOR);
          const fetchName = entityNameFetchers[key.slice(0, separatorIndex)];

          if (!fetchName) return [key, ''];

          try {
            return [
              key,
              await fetchName(Number(key.slice(separatorIndex + 1))),
            ];
          } catch {
            return [key, ''];
          }
        })
      ).then((resolved) => {
        if (!active) return;

        setNames((previous) => ({
          ...previous,
          ...Object.fromEntries(resolved.filter(([, name]) => name)),
        }));
      });
    }

    return () => {
      active = false;
    };
  }, [entitiesKey]);

  return names;
}
